import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Heading component with semantic HTML and theme-based variants
 */
const headingVariants = cva(
    "font-bold text-foreground tracking-tight",
    {
        variants: {
            level: {
                h1: "text-5xl leading-tight",
                h2: "text-4xl leading-tight",
                h3: "text-3xl leading-tight",
                h4: "text-2xl leading-normal",
                h5: "text-xl leading-normal",
                h6: "text-lg leading-normal",
            },
        },
        defaultVariants: {
            level: "h2",
        },
    }
)

const Heading = React.forwardRef(
    ({ className, level = "h2", as, ...props }, ref) => {
        const Comp = as || level
        return (
            <Comp
                ref={ref}
                className={cn(headingVariants({ level, className }))}
                {...props}
            />
        )
    }
)
Heading.displayName = "Heading"

/**
 * Text component for body content
 */
const textVariants = cva(
    "text-foreground",
    {
        variants: {
            size: {
                xs: "text-xs",
                sm: "text-sm",
                base: "text-base",
                lg: "text-lg",
                xl: "text-xl",
            },
            variant: {
                default: "text-foreground",
                muted: "text-muted-foreground",
                accent: "text-accent-foreground",
                destructive: "text-destructive",
            },
            weight: {
                normal: "font-normal",
                medium: "font-medium",
                semibold: "font-semibold",
                bold: "font-bold",
            },
        },
        defaultVariants: {
            size: "base",
            variant: "default",
            weight: "normal",
        },
    }
)

const Text = React.forwardRef(
    ({ className, size, variant, weight, as: Comp = "p", ...props }, ref) => {
        return (
            <Comp
                ref={ref}
                className={cn(textVariants({ size, variant, weight, className }))}
                {...props}
            />
        )
    }
)
Text.displayName = "Text"

export { Heading, Text }
